import React, { FC, useContext } from "react";
import { GridLegacy as Grid, Typography, Box } from "@mui/material";
import EditableGroup, { EditableGroupType } from "@/components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "@/components/editable/EditableField/EditableField";
import { IPreferences, IDriverAvailabilityOptions } from "@/logic/api";
import { isSysAdmin } from "@/utility/utils";
import { SessionContext } from "@/utility/useSession";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

interface DriverAvailabilityRuleOptionsProps {
    onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const DriverAvailabilityRuleOptions: FC<DriverAvailabilityRuleOptionsProps> = ({ onSave }) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const isTaskAssignmentMarksOnShiftReleased = useReleaseManagement(ReleaseFlag.releaseTaskAssignmentMarksOnShift);
    const isDriverAvailabilityRulesForAdminsReleased = useReleaseManagement(ReleaseFlag.releaseDriverAvailabilityRulesForAdmins);

    if (!session.current.site || (!isSysAdmin(session) && !isDriverAvailabilityRulesForAdminsReleased)) {
        return null;
    }

    const driverAvailabilityFields = (data: IPreferences): IDriverAvailabilityOptions => {
        if (!data.driver_availability_options?.properties) {
            data.driver_availability_options = { properties: {} };
        }
        return data.driver_availability_options;
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1.5 }}>
                {t("driverAvailability.title")}
            </Typography>
            <Box
                data-testid="driver-availability-rule-options"
                sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    position: "relative",
                    isolation: "isolate",
                }}
            >

                <EditableGroup<IPreferences>
                    id="driverAvailabilityRuleOptions"
                    editType={EditableGroupType.switchableEditing}
                    data={session.current.site.preferences}
                    onDataChanged={(data) =>
                        onSave({
                            driver_availability_options: data.driver_availability_options
                        })
                    }
                    testId="driver-availability-rule-options"
                >
                    <Grid container spacing={2}>
                        <Grid item sm={6} xs={12}>
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing
                                inputType={EditableFieldTypes.switch}
                                label={t("driverAvailability.rulesOptions.keyScan")}
                                onProvide={(data) =>
                                    Boolean(
                                        data.driver_availability_options?.properties?.key_scan_enabled?.required
                                    )
                                }
                                onChange={(data, value) =>
                                    driverAvailabilityFields(data).properties.key_scan_enabled = {
                                        required: value
                                    }
                                }
                            />
                        </Grid>

                        <Grid item sm={6} xs={12}>
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing
                                inputType={EditableFieldTypes.switch}
                                label={t("driverAvailability.rulesOptions.mobileLogin")}
                                onProvide={(data) =>
                                    Boolean(
                                        data.driver_availability_options?.properties?.mobile_login_enabled?.required
                                    )
                                }
                                onChange={(data, value) =>
                                    driverAvailabilityFields(data).properties.mobile_login_enabled = {
                                        required: value
                                    }
                                }
                            />
                        </Grid>

                        <Grid item sm={6} xs={12}>
                            <EditableField<IPreferences, boolean>
                                useFullWidthWhenEditing
                                inputType={EditableFieldTypes.switch}
                                label={t("driverAvailability.rulesOptions.mobileLogout")}
                                onProvide={(data) =>
                                    Boolean(
                                        data.driver_availability_options?.properties?.mobile_logout_enabled?.required
                                    )
                                }
                                onChange={(data, value) =>
                                    driverAvailabilityFields(data).properties.mobile_logout_enabled = {
                                        required: value
                                    }
                                }
                            />
                        </Grid>

                        {isTaskAssignmentMarksOnShiftReleased && (
                            <Grid item sm={6} xs={12}>
                                <EditableField<IPreferences, boolean>
                                    useFullWidthWhenEditing
                                    inputType={EditableFieldTypes.switch}
                                    label={t("driverAvailability.rulesOptions.assignTaskMarksOnShift")}
                                    onProvide={(data) =>
                                        Boolean(
                                            data.driver_availability_options?.properties?.task_assignment_marks_on_shift?.required
                                        )
                                    }
                                    onChange={(data, value) =>
                                        driverAvailabilityFields(data).properties.task_assignment_marks_on_shift = {
                                            required: value
                                        }
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </EditableGroup>
            </Box>
        </Box>
    );
};
